import React, { useMemo, useRef, useEffect, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Popup,
  Tooltip,
} from "react-leaflet";
import L from "leaflet";
import { formatPhoneNumber } from "../../utils/phoneNumberUtils";

const apiBaseUrl = process.env.REACT_APP_API_URL;

const numberedIcon = (n, color = "#004a7e") =>
  L.divIcon({
    className: "",
    html: `<div style="background:${color};color:#fff;border-radius:50%;width:30px;height:30px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4);font-weight:600;font-size:13px;">${n}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });

const flagIcon = (label, color) =>
  L.divIcon({
    className: "",
    html: `<div style="background:${color};color:#fff;border-radius:6px;padding:4px 8px;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4);font-weight:600;font-size:11px;white-space:nowrap;">${label}</div>`,
    iconSize: [null, null],
    iconAnchor: [22, 12],
  });

const startIcon = flagIcon("START", "#16a34a");
const endIcon = flagIcon("END", "#dc2626");

const OptimizeRouteResult = ({
  result,
  timezone,
  formatTime,
  uniqueCode,
  onEdit,
  onApplied,
}) => {
  const mapRef = useRef(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);

  const handleApply = async () => {
    if (applying) return;
    const ok = window.confirm(
      `Accept route and send pickup times via SMS to ${result.orderedStops.length} passenger(s)?`,
    );
    if (!ok) return;
    setApplying(true);
    setApplyError(null);
    try {
      const resp = await fetch(
        `${apiBaseUrl}/api/event-participations/${uniqueCode}/apply-route`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sendSms: true,
            stops: result.orderedStops.map((s) => ({
              eventParticipationId: s.eventParticipationId,
              pickupTime: s.pickupTime,
            })),
          }),
        },
      );
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(body?.message || "Could not apply route.");
      }
      const parts = [`Pickup times saved for ${body.updated} passenger(s).`];
      if (body.smsSent) parts.push(`${body.smsSent} SMS sent.`);
      if (body.smsFailed) parts.push(`${body.smsFailed} SMS failed.`);
      if (body.errors?.length) parts.push(body.errors.join(" "));
      alert(parts.join(" "));
      onApplied?.();
    } catch (err) {
      setApplyError(err.message || "Could not apply route.");
    } finally {
      setApplying(false);
    }
  };

  const polylinePositions = useMemo(
    () => (result?.polyline || []).map((p) => [p.lat, p.lng]),
    [result],
  );

  const bounds = useMemo(() => {
    const all = [
      [result.start.lat, result.start.lng],
      [result.destination.lat, result.destination.lng],
      ...result.orderedStops.map((s) => [s.lat, s.lng]),
    ];
    return L.latLngBounds(all);
  }, [result]);

  useEffect(() => {
    if (mapRef.current && bounds) {
      mapRef.current.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [bounds]);

  return (
    <div className="p-4 space-y-4">
      {result.warnings?.length > 0 && (
        <div className="alert alert-warning text-sm">
          {result.warnings.map((w, i) => (
            <div key={i}>⚠️ {w}</div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryTile
          label="Depart"
          value={formatTime(result.departTime, timezone)}
        />
        <SummaryTile
          label="Arrive"
          value={formatTime(result.arriveBy, timezone)}
        />
        <SummaryTile
          label="Distance"
          value={`${result.totalDistanceMiles} mi`}
        />
        <SummaryTile
          label="Drive time"
          value={`${result.totalDurationMinutes} min`}
        />
      </div>

      <div className="rounded-lg overflow-hidden border">
        <MapContainer
          ref={mapRef}
          bounds={bounds}
          style={{ width: "100%", height: "45vh" }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {polylinePositions.length > 1 && (
            <Polyline
              positions={polylinePositions}
              pathOptions={{ color: "#004a7e", weight: 4, opacity: 0.8 }}
            />
          )}
          <Marker
            position={[result.start.lat, result.start.lng]}
            icon={startIcon}
          >
            <Popup>
              <div className="text-sm">
                <div className="font-semibold">Start</div>
                {result.start.address && (
                  <div className="text-gray-600">{result.start.address}</div>
                )}
                <div className="text-gray-500 text-xs mt-1">
                  Depart by {formatTime(result.departTime, timezone)}
                </div>
              </div>
            </Popup>
          </Marker>
          {result.orderedStops.map((s) => (
            <Marker
              key={s.eventParticipationId}
              position={[s.lat, s.lng]}
              icon={numberedIcon(s.sequence)}
            >
              <Tooltip permanent direction="top" offset={[0, -16]}>
                {s.sequence}. {s.name} · {formatTime(s.pickupTime, timezone)}
              </Tooltip>
              <Popup>
                <div className="text-sm">
                  <div className="font-semibold">
                    {s.sequence}. {s.name}
                  </div>
                  <div className="text-gray-700">
                    {s.rideLocation || s.pickupGeocodedAddress}
                  </div>
                  {s.phone && (
                    <div className="text-gray-500 text-xs mt-1">
                      {formatPhoneNumber(s.phone)}
                    </div>
                  )}
                  <div className="text-[#004a7e] text-xs mt-1 font-semibold">
                    Pickup @ {formatTime(s.pickupTime, timezone)}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
          <Marker
            position={[result.destination.lat, result.destination.lng]}
            icon={endIcon}
          >
            <Popup>
              <div className="text-sm">
                <div className="font-semibold">Destination</div>
                {result.destination.address && (
                  <div className="text-gray-600">
                    {result.destination.address}
                  </div>
                )}
                <div className="text-gray-500 text-xs mt-1">
                  Arrive by {formatTime(result.arriveBy, timezone)}
                </div>
              </div>
            </Popup>
          </Marker>
        </MapContainer>
      </div>

      <div>
        <h4 className="font-semibold text-sm mb-2">Schedule</h4>
        <ol className="space-y-2">
          <li className="flex items-start gap-3 text-sm">
            <span className="bg-green-600 text-white text-xs font-semibold px-2 py-1 rounded">
              DEPART
            </span>
            <div className="flex-1">
              <div className="font-medium">
                {formatTime(result.departTime, timezone)}
              </div>
              {result.start.address && (
                <div className="text-gray-600 text-xs">
                  {result.start.address}
                </div>
              )}
            </div>
          </li>
          {result.orderedStops.map((s) => (
            <li
              key={s.eventParticipationId}
              className="flex items-start gap-3 text-sm"
            >
              <span className="bg-[#004a7e] text-white text-xs font-semibold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">
                {s.sequence}
              </span>
              <div className="flex-1">
                <div className="font-medium">
                  {formatTime(s.pickupTime, timezone)} — {s.name}
                  {s.phone ? ` · ${formatPhoneNumber(s.phone)}` : ""}
                </div>
                <div className="text-gray-600 text-xs">
                  {s.rideLocation || s.pickupGeocodedAddress}
                </div>
              </div>
            </li>
          ))}
          <li className="flex items-start gap-3 text-sm">
            <span className="bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded">
              ARRIVE
            </span>
            <div className="flex-1">
              <div className="font-medium">
                {formatTime(result.arriveBy, timezone)}
              </div>
              {result.destination.address && (
                <div className="text-gray-600 text-xs">
                  {result.destination.address}
                </div>
              )}
            </div>
          </li>
        </ol>
      </div>

      {applyError && (
        <div className="alert alert-error text-sm">{applyError}</div>
      )}

      <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-2 pt-2 border-t">
        <button
          className="btn btn-ghost btn-sm w-full sm:w-auto"
          onClick={onEdit}
          disabled={applying}
        >
          ← Edit inputs
        </button>
        <button
          className="btn btn-sm bg-[#004a7e] text-white hover:bg-[#003a64] w-full sm:w-auto whitespace-normal text-center leading-tight"
          onClick={handleApply}
          disabled={applying}
          title="Save these pickup times to passengers and send them SMS notifications"
        >
          {applying ? "Sending…" : "Accept Route & Send Pickup Times"}
        </button>
      </div>
    </div>
  );
};

const SummaryTile = ({ label, value }) => (
  <div className="bg-gray-50 border rounded p-2 text-center">
    <div className="text-[10px] uppercase tracking-wide text-gray-500">
      {label}
    </div>
    <div className="font-semibold text-sm">{value}</div>
  </div>
);

export default OptimizeRouteResult;
